import { Badge } from 'flowbite-react';
import React, { useCallback, useEffect, useState } from 'react';
import { HiCheck } from 'react-icons/hi';
import { IoMdClose } from 'react-icons/io';
import DataGrid from '../../components/DataGrid';
import CodeSpinner from '../../components/CodeSpinner';
import { ClienteGrupoService } from '../../services/ClienteGrupoService';
import { ClienteGrupoModel } from '../../models/ClienteGrupoModel';
import { CustomersGroupsCTA } from './components/CTA';
import { FormStateType } from '../../enums';
import { Popup } from '../../components/Popup';
import CustomersGroupsModal from './components/Modal';
import { ModalProps } from '../../types';
import { ShowToast } from '../../components/CodeToast';

const CustomersGroupsPage: React.FC = () => {
  const [isLoadingData, setIsLoadingData] = useState<boolean>(true);
  const [pageIndex, setPageIndex] = useState<number>(1);
  const [pageCount, setPageCount] = useState<number>(1);
  const [dataSource, setDataSource] = useState<ClienteGrupoModel[]>([]);

  const [selectedModel, setSelectedModel] = useState<ClienteGrupoModel | null>(null);
  const [showPopupDelete, setShowPopupDelete] = useState<boolean>(false);
  const [modalState, setModalState] = useState<ModalProps>({ show: false, state: FormStateType.view, id: 0 });

  const onFormLoad = useCallback(async () => {
    setIsLoadingData(true);
    ClienteGrupoService.get({ page: pageIndex, limit: 100 }).then((response) => {
      setDataSource(response.data ?? []);
      setPageIndex(response.pagination.page);
      setPageCount(response.pagination.pageCount);
    }).finally(() => setIsLoadingData(false));
  }, [setDataSource, setIsLoadingData, pageIndex, setPageIndex, setPageCount]);

  useEffect(() => {
    onFormLoad()
  }, [onFormLoad]);

  const add = () => setModalState({ ...modalState, state: FormStateType.add, show: true, id: 0 });

  const viewOrEdit = (edit: boolean) => {
    if (selectedModel?.id === undefined) {
      ShowToast({ message: `Nenhum registro selecionado para ${edit ? 'edição' : 'visualização'}` });
      return;
    }

    setModalState({ state: edit ? FormStateType.edit : FormStateType.view, show: true, id: selectedModel.id });
  }

  const deleteItem = async () => {
    try {
      let response = await ClienteGrupoService.delete(selectedModel!.id!);
      if (!response.success) {
        console.log(response.messages);
        return;
      }

      await onFormLoad();
    } catch (error) {
      console.log(error);
    }
    finally {
      setShowPopupDelete(false);
    }
  }


  const actionButtons = (): JSX.Element => {
    return (
      <CustomersGroupsCTA 
        modelState={[selectedModel, setSelectedModel]}
        onAdd={() => add()}
        onEdit={async () => viewOrEdit(true)}
        onView={async () => viewOrEdit(false)}
        onDelete={() => setShowPopupDelete(true)} />
    );
  };

  return (
    <div>
      <CodeSpinner hidden={!isLoadingData} fullPage={true} />

      <div className='mt-5' hidden={isLoadingData}>
        <DataGrid
          multiSelect={false}
          sortedColumn='nome'

          keyColumn='id'
          columns={[
            { propertyName: 'id', title: '#' },
            {
              propertyName: 'ativo', title: 'Ativo', columnAlignment: 'center',
              onFormat(value) {
                return (
                  <div className='flex justify-center flex-wrap items-center gap-2'>
                    {!(value as boolean) ? <Badge size='sm' color='failure' icon={IoMdClose} /> : <Badge icon={HiCheck} size="sm" color='success' />}
                  </div>
                )
              },
            },
            { propertyName: 'descricao', title: 'Descrição' },
            {
              propertyName: 'percentualAutoRendimento', columnAlignment: 'right', title: '% Auto Rendimento',
              onFormat(value) { return `% ${Number(value).toFixed(2)}` }
            },
          ]}
          data={dataSource}
          caption={{
            title: 'Grupos de cliente',
            resume: 'Listagem de grupos de cliente',
            children: actionButtons()
          }}
          pagination={{
            pageCount: pageCount,
            pageIndex: pageIndex,
            limit: 100,
            onPageChange: (pageIndex) => setPageIndex(pageIndex)
          }}
          onRowSelect={(selectedRow) => setSelectedModel(selectedRow[0] as ClienteGrupoModel)}
        />
      </div>

      <CustomersGroupsModal
        show={modalState.show}
        id={modalState.id}
        state={modalState.state}
        title="Cadastro de Grupos"
        onClose={() => setModalState({ ...modalState, show: false })}
        onSave={async () => {
          setModalState({ ...modalState, show: false });
          await onFormLoad();
        }}
      />

      <Popup isShowing={showPopupDelete} message="Confirma a exclusão do registro selecionado?"
        isDestructive={true} onConfirm={async () => await deleteItem()}
        onCancel={() => setShowPopupDelete(false)} />
    </div>
  )
}

export default CustomersGroupsPage;